import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppPages } from '@app/app.pages';
import { DirectusErrorCode } from '@app/enum/directus-error-codes.enum';
import { DirectusError } from '@app/interfaces/directus-error.interface';
import { DirectusQuery } from '@app/interfaces/directus-query.interface';
import { createDirectus, createItem, readItem, readItems, rest, RestClient } from '@directus/sdk';
import { environment } from '@environment/environment';
import { DirectusSchema } from '@interfaces/directus-schema.interface';

@Injectable({
  providedIn: 'root'
})
export class DirectusService {
  private readonly router: Router = inject(Router);
  public client: RestClient<DirectusSchema> = createDirectus(environment.directusUrl).with(rest());

  public async readItem<T>(
    collection: keyof DirectusSchema,
    id: string,
    query?: Partial<DirectusQuery>
  ): Promise<T | null> {
    return await this.client
      .request<T>(readItem(collection, id, query as never))
      .catch((error: unknown) => this.handleError(error as DirectusError));
  }

  public async readItems<T>(collection: keyof DirectusSchema, query?: Partial<DirectusQuery>): Promise<T[]> {
    return await this.client.request<T[]>(readItems(collection, query as never));
  }

  public async createItem<T>(collection: keyof DirectusSchema, data: Partial<T>): Promise<T> {
    return await this.client.request<T>(createItem(collection, data as never));
  }

  private handleError(error: DirectusError): null {
    if (!error.errors) {
      this.router.navigate([AppPages.ServerErrorPage]);
    }
    switch (error.errors[0].extensions.code) {
      case DirectusErrorCode.Forbidden:
        this.router.navigate([AppPages.NotFoundPage]);
        break;
      default:
        this.router.navigate([AppPages.ServerErrorPage]);
        break;
    }
    return null;
  }
}
